/**
 * EditorHistory — Snapshot-based undo/redo for the cinematic editor.
 * Stores JSON snapshots of the full sequence before each mutation.
 * Follows the same pattern as MapEditorScene's undo system.
 */
import { EventBus } from '@/events/EventBus';
import { EditorEvents } from './EditorTypes';
import type { EditorState } from './EditorTypes';

const MAX_UNDO = 40;

export class EditorHistory {
  private undoStack: string[] = [];
  private redoStack: string[] = [];
  private state: EditorState;

  /**
   * Debounce guard for STEP_UPDATED (fires on every slider drag).
   * Only push a snapshot on the FIRST update after a selection change or 500ms idle.
   */
  private lastSnapshotTime = 0;
  private static DEBOUNCE_MS = 500;

  constructor(state: EditorState) {
    this.state = state;
  }

  /** Call BEFORE any mutation. Captures current sequence as undo snapshot. */
  pushUndo(): void {
    const snap = JSON.stringify(this.state.sequence);
    this.undoStack.push(snap);
    if (this.undoStack.length > MAX_UNDO) this.undoStack.shift();
    // Any new mutation invalidates redo
    this.redoStack.length = 0;
    this.emitChanged();
  }

  /**
   * Debounced pushUndo — only snapshots if 500ms elapsed since last snapshot.
   * Use this for high-frequency mutations (slider drags, continuous camera edits).
   */
  pushUndoDebounced(): void {
    const now = Date.now();
    if (now - this.lastSnapshotTime >= EditorHistory.DEBOUNCE_MS) {
      this.pushUndo();
      this.lastSnapshotTime = now;
    }
  }

  /** Reset debounce timer (call on step selection change). */
  resetDebounce(): void {
    this.lastSnapshotTime = 0;
  }

  /** Restore previous state. Returns true if undo was performed. */
  undo(): boolean {
    if (this.undoStack.length === 0) return false;
    // Save current state to redo
    this.redoStack.push(JSON.stringify(this.state.sequence));
    // Pop previous state
    const snap = this.undoStack.pop()!;
    this.applySnapshot(snap);
    this.emitChanged();
    return true;
  }

  /** Re-apply undone state. Returns true if redo was performed. */
  redo(): boolean {
    if (this.redoStack.length === 0) return false;
    // Save current state to undo
    this.undoStack.push(JSON.stringify(this.state.sequence));
    // Pop redo state
    const snap = this.redoStack.pop()!;
    this.applySnapshot(snap);
    this.emitChanged();
    return true;
  }

  /** Get stack sizes for toolbar display */
  getCounts(): { undo: number; redo: number } {
    return { undo: this.undoStack.length, redo: this.redoStack.length };
  }

  /** Clear all history (e.g. on sequence switch) */
  clear(): void {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    this.lastSnapshotTime = 0;
    this.emitChanged();
  }

  private applySnapshot(snap: string): void {
    this.state.sequence = JSON.parse(snap);
    // Clamp selection index
    if (this.state.selectedStepIndex >= this.state.sequence.steps.length) {
      this.state.selectedStepIndex = this.state.sequence.steps.length - 1;
    }
    // Notify all components to rebuild
    EventBus.emit(EditorEvents.SEQUENCE_SWITCHED);
  }

  private emitChanged(): void {
    EventBus.emit(EditorEvents.HISTORY_CHANGED);
  }
}
